import React, { useState, useRef, useEffect } from 'react';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { useAuth } from '../auth-provider';
import { getDatetimeDisplayString } from '../../utils/util';
import { AuthChallange } from '../../data/enums';

interface Props {
  children: React.ReactNode;
}

// https://stackoverflow.com/questions/12710905/how-do-i-dynamically-assign-properties-to-an-object-in-typescript
interface IAxiosInstance extends AxiosInstance {
  [key: string]: any;
}

interface AmaWorkflowValues {  
  
  clients: Array<IClient> | null;
  selectedClient: IClient | null;  
  clientsLastUpdated: string | null;
  clientsError: string | null;
  clientsLoading: boolean;

  loadClients: () => Promise<boolean>;
  addClient: (client: IClient) => Promise<boolean>;
  updateClient: (client: IClient) => Promise<boolean>;
  deleteClient: (id: string) => Promise<boolean>;
  setSelectedClient: (id: string | null) => void;
  clearClientErrors: () => void;

  snapshots: Array<ISnapshot> | null;
  selectedSnapshot: ISnapshot | null;
  snapshotsLastUpdated: string | null;
  snapshotsError: string | null;
  snapshotsLoading: boolean | null;
  
  loadSnapshots: () => Promise<boolean>;
  addSnapshot: (clientId: string, snapshotDate: string) => Promise<boolean>;
  updateSnapshot: (snapshot: ISnapshot) => Promise<boolean>;
  runSnapshotSilverJob: (clientId: string, snapshotDate: string) => Promise<boolean>;
  saveSnapshotToCensus: (clientId: string, snapshotDate: string) => Promise<boolean>;
  deleteSnapshot: (clientId: string, snapshotDate: string) => Promise<boolean>;
  deleteSnapshotFile: (clientId: string, snapshotDate: string, category: string) => Promise<boolean>;
  setSelectedSnapshot: (clientId: string | null, snapshotDate: string | null) => void;
  clearSnapshotErrors: () => void;
}

const AmaWorkflowContext = React.createContext<undefined | AmaWorkflowValues>(undefined);
const Provider = AmaWorkflowContext.Provider;

function AmaWorkflowProvider({ children }: Props) {

  const { user } = useAuth();
  const axiosInstance = useRef<IAxiosInstance | null>(null);
  const abortController = useRef<AbortController>(new AbortController());
  
  const [clients, setClients] = useState<Array<IClient> | null>(null);
  const [selectedClient, setSelectedClient] = useState<IClient | null>(null);
  const [clientsLastUpdated, setClientsLastUpdated] = useState<string>('');
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [clientsLoading, setClientsLoading] = useState<boolean>(false);
  
  const [snapshots, setSnapshots] = useState<Array<ISnapshot> | null>(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState<ISnapshot | null>(null);
  const [snapshotsLastUpdated, setSnapshotsLastUpdated] = useState<string>('');
  const [snapshotsError, setSnapshotsError] = useState<string | null>(null);
  const [snapshotsLoading, setSnapshotsLoading] = useState<boolean>(false);

  // console.log(`AmaWorkflowProvider.render`);
  useEffect(() => {
    //console.log(`AmaWorkflowProvider.mount`);
    return () => { 
      //console.log(`AmaWorkflowProvider.unmount`); 
      if(!abortController.current) return;
      abortController.current.abort();
    };
  },[]);

  useEffect(() => {
    if (!user) return;
    //console.log(`AmaWorkflowProvider.useEffect[User] :: User has updated`);
    axiosInstance.current = null;
    initAxiosInstance();
    // Note: Auto-initialization of loadClients/loadSnapshots removed to prevent duplicate API calls.
    // AMA v2 provider handles data loading on startup. Use explicit method calls if v1 data is needed.

  },[user]);

  const initAxiosInstance = () => {
    if (!user || axiosInstance.current) return;
    // console.log(`AmaWorkflowProvider.initAxiosInstance :: initializing axios instance... :: ${user.lastUpdated}`);
    axiosInstance.current = axios.create({
      baseURL: process.env.REACT_APP_AMA_API_URL!,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': user.idToken
      }
    });
  };

  const loadClients = async () : Promise<boolean> => {
    
    if (!user) return false;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    let isSuccess = true;

    try {
  
      // Api call, load data
      setClientsLoading(true);
      const response = await axiosInstance.current!.get(
        '/clients', 
        { signal: abortController.current.signal });
        
      // Map IClientApi to IClient
      const clients = mapClientsFromApi(response.data['Items']);

      // Update state      
      setClients(clients);
      setClientsLastUpdated(getDatetimeDisplayString(new Date()));
      clearClientErrors();

      // console.log(`AmaWorkflowProvider.loadClients :: ${response.data['Items'].length} records found!`);

    } catch (err: any) {

      setClientsError(`There was a problem loading clients!`);
      console.error(`AmaWorkflowProvider.loadClients :: error: ${err.message}`);
      isSuccess = false;

    } finally {

      setClientsLoading(false);

    }

    return isSuccess;
  };

  const addClient = async (client: IClient) : Promise<boolean> => {

    if (!user) return false;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    let isSuccess = true;

    try {
 
      // Api call, load data
      setClientsLoading(true);

      // Map from IClient to IClientApi
      const mappedClient : IClientApi = mapClientToApi(client);

      // API call
      const response = await axiosInstance.current!.post(
        `/clients/${client.ClientId}`, 
        JSON.stringify(mappedClient),
        { signal: abortController.current.signal });

      // Reload clients (we can get fancy later, for now simply reload entire collection)
      await loadClients();

      // console.log(`AmaWorkflowProvider.addClient :: ${JSON.stringify(client)}`);

    } catch (err: any) {

      setClientsError(`A client already exists for the ID: ${client.ClientId}`);
      console.error(`AmaWorkflowProvider.addClient :: error: ${err.message}`);
      isSuccess = false;

    } finally {

      setClientsLoading(false);

    }

    return isSuccess;
  };

  const updateClient = async (client: IClient) : Promise<boolean> => {

    if (!user) return false;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    let isSuccess = true;

    try {
  
      // Api call, load data
      setClientsLoading(true);

      // Map from IClient to IClientApi
      const mappedClient : IClientApi = mapClientToApi(client);

      // API call
      const response = await axiosInstance.current!.put(
        `/clients/${mappedClient.ClientId}`, 
        JSON.stringify(mappedClient),
        { signal: abortController.current.signal });

      // Reload clients
      await loadClients();
      
      // console.log(`AmaWorkflowProvider.updateClient :: response: ${JSON.stringify(response)}`);

    } catch (err: any) {

      setClientsError('There was a problem updating client record.');
      console.error(`AmaWorkflowProvider.updateClient :: error: ${err.message}`);
      isSuccess = false;

    } finally {

      setClientsLoading(false);      

    }

    return isSuccess;
  };

  const deleteClient = async (id: string) : Promise<boolean> => {

    if (!user) return false;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    let isSuccess = true;
    
    try {
          
      // Api call, load data
      setClientsLoading(true);
      const response = await axiosInstance.current!.delete(
        `/clients/${id}`, 
        { signal: abortController.current.signal });

      // Reload both clients and snapshots
      await Promise.all([loadClients(), loadSnapshots()]);

      // console.log(`AmaWorkflowProvider.deleteClient :: ${id}`);

    } catch (err: any) {

      setClientsError(err.message);
      console.error(`AmaWorkflowProvider.deleteClient :: error: ${err.message}`);      
      isSuccess = false;

    } finally {

      setClientsLoading(false);      

    }

    return isSuccess;
  };

  const setClient = (id: string | null) => {
    if (!id) {
      setSelectedClient(null);
      return;
    }

    const client = clients?.find((item) => item.ClientId === id);
    if (client) setSelectedClient(client);
  };

  const clearClientErrors = () => {
    setClientsError(null);
  };

  const loadSnapshots = async () => {

    if (!user) return false;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    let isSuccess = true;

    try {        
  
      // Api call, load data
      setSnapshotsLoading(true);
      const response = await axiosInstance.current!.get(
        '/snapshots', 
        { signal: abortController.current.signal });
        
      // Map ISnapshotApi to ISnapshot
      const snapshots = mapSnapshotsFromApi(response.data['Items']);

      // Update state and log count
      setSnapshots(snapshots);
      setSnapshotsLastUpdated(getDatetimeDisplayString(new Date()));
      clearSnapshotErrors();

      // console.log(`AmaWorkflowProvider.loadSnapshots :: ${response.data['Items'].length} records found!`);

    } catch (err: any) {

      console.error(`AmaWorkflowProvider.loadSnapshots :: error: ${err.message}`);
      setSnapshotsError(`There was a problem loading snapshots!`);
      isSuccess = false;

    } finally {

      setSnapshotsLoading(false);

    }

    return isSuccess;
  };

  const addSnapshot = async (clientId: string, snapshotDate: string) : Promise<boolean> => {

    if (!user) return false;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    let isSuccess = true;

    try {
 
      // Api call, load data
      setSnapshotsLoading(true);      
      const response = await axiosInstance.current!.post(
        `/snapshots/${clientId}/${snapshotDate}`, 
        { signal: abortController.current.signal });

      // console.log(`AmaWorkflowProvider.addSnapshot :: response: ${JSON.stringify(response)}`);

      // Reload snapshots (we can get fancy later, for now simply reload entire collection)
      await loadSnapshots();

      console.log(`AmaWorkflowProvider.addSnapshot :: snapshot added: ${clientId}/${snapshotDate}`);

    } catch (err: any) {

      let msg = `There has been an error adding a snapshot for: ${clientId}/${snapshotDate}`;
      if (err.response && err.response.data) {
        msg = err.response.data['Message'];
      };

      console.log(`AmaWorkflowProvider.addSnapshot :: error :: ${clientId}/${snapshotDate} :: msg: ${msg}, error: ${err.message}`);
      setSnapshotsError(msg);
      isSuccess = false;

    } finally {

      setSnapshotsLoading(false);

    }

    return isSuccess;
  };

  const updateSnapshot = async (snapshot: ISnapshot) : Promise<boolean> => {

    if (!user) return false;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    let isSuccess = true;

    try {
  
      // Api call, load data
      setSnapshotsLoading(true);

      // TODO - implement
      // const response = await axiosInstance.current!.put(
      //   `/clients/${client.ClientId}`, 
      //   JSON.stringify(client),
      //   { signal: abortController.current.signal });


      // Reload snapshots
      // await loadSnapshots();
      
      // console.log(`AmaWorkflowProvider.updateClient :: response: ${JSON.stringify(response)}`);

    } catch (err: any) {

      setSnapshotsError('todo');
      console.error(`AmaWorkflowProvider.updateSnapshot :: error: ${err.message}`);
      isSuccess = false;

    } finally {

      setSnapshotsLoading(false);      

    }

    return isSuccess;
  };

  const runSnapshotSilverJob = async (clientId: string, snapshotDate: string) : Promise<boolean> => {
    
    if (!user) return false;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    let isSuccess = true;

    try {
  
      // Api call, load data
      setSnapshotsLoading(true);
      const response = await axiosInstance.current!.post(
        `/snapshots/${clientId}/${snapshotDate}/run/silver`, 
        { signal: abortController.current.signal });

      // Reload snapshots
      await loadSnapshots();
      
      // console.log(`AmaWorkflowProvider.updateClient :: response: ${JSON.stringify(response)}`);

    } catch (err: any) {

      setSnapshotsError('todo');
      console.error(`AmaWorkflowProvider.runSnapshotSilverJob :: error: ${err.message}`);
      isSuccess = false;

    } finally {

      setSnapshotsLoading(false);      

    }

    return isSuccess;
  }
  
  const saveSnapshotToCensus = async (clientId: string, snapshotDate: string) : Promise<boolean> => {
    
    if (!user) return false;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    let isSuccess = true;

    try {
  
      // Api call, load data
      setSnapshotsLoading(true);

      const response = await axiosInstance.current!.post(
        `/snapshots/${clientId}/${snapshotDate}/copy-to/census`, 
        { signal: abortController.current.signal });

      // Reload snapshots
      await loadClients();
      
      console.log(`AmaWorkflowProvider.saveSnapshotToCensus :: response: ${JSON.stringify(response)}`);

    } catch (err: any) {

      setSnapshotsError('Error saving snapshot to Census!');
      console.error(`AmaWorkflowProvider.saveSnapshotToCensus :: error: ${err.message}`);
      isSuccess = false;

    } finally {

      setSnapshotsLoading(false);      

    }

    return isSuccess;
  }

  const deleteSnapshot = async (clientId: string, snapshotDate: string) : Promise<boolean> => {

    if (!user) return false;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    let isSuccess = true;
    
    try {
          
      // Api call, load data
      setSnapshotsLoading(true);
      const response = await axiosInstance.current!.delete(
        `/snapshots/${clientId}/${snapshotDate}`, 
        { signal: abortController.current.signal });

      // Reload snapshots
      await loadSnapshots();

      // console.log(`AmaWorkflowProvider.deleteSnapshot :: ${id}`);

    } catch (err: any) {

      let msg = `There has been an error deleting the snapshot for: ${clientId}/${snapshotDate}`;
      if (err.response && err.response.data) {
        msg = err.response.data['Message'];
      };

      console.log(`AmaWorkflowProvider.deleteSnapshot :: error :: ${clientId}/${snapshotDate} :: msg: ${msg}, error: ${err.message}`);
      setSnapshotsError(msg);
      isSuccess = false;

    } finally {

      setSnapshotsLoading(false);      

    }

    return isSuccess;
  };

  const deleteSnapshotFile = async (clientId: string, snapshotDate: string, category: string) : Promise<boolean> => {

    if (!user) return false;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    let isSuccess = true;
    
    try {
          
      // Api call, load data
      setSnapshotsLoading(true);
      const response = await axiosInstance.current!.delete(
        `/snapshots/${clientId}/${snapshotDate}/${category}`, 
        { signal: abortController.current.signal });

      // Reload snapshots
      await loadSnapshots();

      // Log
      console.log(`AmaWorkflowProvider.deleteSnapshotFile :: ${clientId}/${snapshotDate} :: category: ${category}, response: ${response}`);

    } catch (err: any) {

      let msg = `There has been an error deleting the snapshot file for: ${clientId}/${snapshotDate}/${category}`;
      if (err.response && err.response.data) {
        msg = err.response.data['Message'];
      };

      console.log(`AmaWorkflowProvider.deleteSnapshotFile :: error :: msg: ${msg}, error: ${err.message}`);
      setSnapshotsError(msg);
      isSuccess = false;

    } finally {

      setSnapshotsLoading(false);      

    }

    return isSuccess;
  };

  const setSnapshot = (clientId: string | null, snapshotDate: string | null) : void => {
    if (!clientId || !snapshotDate) {
      setSelectedSnapshot(null);
      return;
    }
    
    const snapshot = snapshots?.find((item) => item.ClientId === clientId && item.SnapshotDate === snapshotDate);
    if (snapshot) setSelectedSnapshot(snapshot);
  };

  const clearSnapshotErrors = () => {
    setSnapshotsError(null);
  };

  // Utility function for testing
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  return (
    <Provider value={{ 
      
      clients, 
      selectedClient,
      clientsLastUpdated, 
      clientsLoading,
      clientsError,
      
      loadClients, 
      addClient,
      updateClient,
      deleteClient,
      setSelectedClient: setClient,
      clearClientErrors,

      snapshots, 
      selectedSnapshot, 
      snapshotsLastUpdated, 
      snapshotsLoading,
      snapshotsError,

      loadSnapshots,
      addSnapshot,
      updateSnapshot,
      runSnapshotSilverJob,
      saveSnapshotToCensus,
      deleteSnapshot,
      deleteSnapshotFile,
      setSelectedSnapshot: setSnapshot,
      clearSnapshotErrors
      
      }}>
      {children}
    </Provider>
  );
}

const useAmaWorkflowContext = () => {
  const context = React.useContext(AmaWorkflowContext);
  if (context === undefined) throw new Error('useAmaWorkflowContext must be used within an AmaWorkflowProvider!');
  return context;
};

const mapClientsFromApi = (clients: Array<IClientApi>) : Array<IClient> => {

  const mappedClients : Array<IClient> = [];

  try {
    clients.forEach(item => mappedClients.push(mapClientFromApi(item)));
  } catch (error) {
    console.log(`AmaWorkflowProvider.mapClientsFromApi :: error: ${error}`);
  }

  return mappedClients;
};

const mapClientFromApi = (client: IClientApi) : IClient => {
  
  const mappedClient = { ...client } as IClient;
  const manifestItems : Array<IManifestItem> = [];

  if (client && client.AmaPipeline) {
    const manifestItemsObj : any = client.AmaPipeline?.SourceFiles; // eslint-disable-line    
    for (const key in manifestItemsObj) {
        const manifestItem = manifestItemsObj[key] as IManifestItem;
        manifestItem.Category = key;
        manifestItems.push(manifestItem);
    }

    client.AmaPipeline.SourceFiles = manifestItems;
  }

  return mappedClient;
};

const mapClientToApi = (client: IClient) : IClientApi => {
  
  const mappedClient = { ...client } as IClientApi;
  const manifestItemsMap : any = {};  // eslint-disable-line

  if (client && client.AmaPipeline && client.AmaPipeline.SourceFiles) {
    for (const manifestItem of client.AmaPipeline.SourceFiles) {
      const { Category } = manifestItem;
      manifestItemsMap[Category] = {
        'PreprocessPath': '',
        'PreprocessGlueJobName': '',
        'BronzeTableName': '',        
        'BronzePath': '',
        'PiiAttributes': manifestItem.PiiAttributes,        
        'IsCollectionComplete': false,
        'Error': '',
        'ErrorDetail': ''
      };      
    }

    if (mappedClient.AmaPipeline) {
      mappedClient.AmaPipeline.SourceFiles = manifestItemsMap;
    }    
  }

  return mappedClient;
};

const mapSnapshotsFromApi = (snapshots: Array<ISnapshotApi>) : Array<ISnapshot> => {

  const mappedSnapshots : Array<ISnapshot> = [];

  try {
    snapshots.forEach(item => mappedSnapshots.push(mapSnapshotFromApi(item)));
  } catch (error) {
    console.log(`AmaWorkflowProvider.mapSnapshotsFromApi :: error: ${error}`);
  }

  return mappedSnapshots;
};

const mapSnapshotFromApi = (snapshot: ISnapshotApi) : ISnapshot => {

  const mappedSnapshot = { ...snapshot } as ISnapshot;
  const manifestItems : Array<IManifestItem> = [];

  if (snapshot) {
    const manifestItemsObj : any = snapshot.SourceFiles; // eslint-disable-line    
    for (const key in manifestItemsObj) {
        const manifestItem = manifestItemsObj[key] as IManifestItem;
        manifestItem.Category = key;
        manifestItems.push(manifestItem);
    }

    mappedSnapshot.SourceFiles = manifestItems;
  }

  return mappedSnapshot;
};

export {
  AmaWorkflowProvider,
  useAmaWorkflowContext
};
      
